import { FC } from "react";
import { Box, Typography } from "@mui/material";

import { EdiObject } from "../../../../types/claims";
import { formatCurrency } from "../../../helpers/dataHelper";
import Row from "../../../ui/core/Row";
import ChevronRightIcon from "../../../icons/ChevronRightIcon";
import CardChip from "./CardChip";
import { EDI_CHIP_STYLES, getCardSx } from "./ediCardStyles";
import {
  MATCHED_MANUAL_CHIP,
  getManualPaymentChip,
  getEdiObjectLabel,
  getEdiObjectChip,
  getEdiObjectDate,
  getEdiObjectAmounts,
} from "./ediObjectHelpers";
import { isManualPayment } from "./record-payment/manualPayments";

// A clickable card summarizing one EDI object (837 claim, 835 payment or a manual payment) in the Claim timeline
const EdiObjectCard: FC<{
  ediObj: EdiObject;
  onClick: () => void;
  /** A remit of the corrected claim on the original's timeline: badged "from #CHILD". */
  fromClaimNumber?: string;
}> = ({ ediObj, onClick, fromClaimNumber }) => {
  const chip = getEdiObjectChip(ediObj);
  const { billed, paid } = getEdiObjectAmounts(ediObj);
  const isManual = isManualPayment(ediObj);

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      data-manual-payment={isManual || undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      sx={getCardSx(chip ? EDI_CHIP_STYLES[chip.kind].color : "#E5E7EB")}
    >
      <Box sx={{ minWidth: 0 }}>
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
            {getEdiObjectLabel(ediObj)}
          </Typography>
          {chip && <CardChip chip={chip} />}
          {isManual && <CardChip chip={getManualPaymentChip(ediObj)} />}
          {ediObj.matchedManual && <CardChip chip={MATCHED_MANUAL_CHIP} />}
          {fromClaimNumber && (
            <CardChip
              chip={{ label: `from #${fromClaimNumber}`, kind: "default" }}
            />
          )}
        </Row>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.7rem" }}
        >
          {getEdiObjectDate(ediObj)}
        </Typography>
      </Box>

      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
      >
        {(billed !== undefined || paid !== undefined) && (
          <Box sx={{ textAlign: "right" }}>
            {billed !== undefined && (
              <Typography sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Billed{" "}
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {formatCurrency(billed)}
                </Box>
              </Typography>
            )}
            {paid !== undefined && (
              <Typography sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Paid{" "}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: paid >= 0 ? "success.main" : "text.primary",
                  }}
                >
                  {formatCurrency(paid)}
                </Box>
              </Typography>
            )}
          </Box>
        )}
        <ChevronRightIcon className="w-4 h-4 text-gray-400" />
      </Box>
    </Box>
  );
};

export default EdiObjectCard;
